import type { Holding } from "@/data/fund-detail-types";
/**
 * Slide-out side panel showing the full holdings list. Ported from the dane
 * etf-website `components/fund-holdings-side-panel.tsx`.
 */
import type React from "react";
import { useEffect, useRef, useState } from "react";

export type FundHoldingsTabPill = "TRS" | "T-BILL" | "CASH";

export interface FundHoldingsTabRow {
  name: string;
  description: string;
  pill: FundHoldingsTabPill | null;
  weightPct: number;
  highlight?: boolean;
}

export interface FundHoldingsTabData {
  rows: FundHoldingsTabRow[];
  callout: { label: string; body: string };
  asOfLabel: string;
  ctaHref?: string;
  ctaLabel?: string;
  loadingText?: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  holdings: Holding[];
  holdingsCount: number;
  dateLabel: string;
  ticker: string;
  fundName?: string;
  showFounder?: boolean;
  founderLookup?: Record<string, string>;
  fundHoldingsTab?: FundHoldingsTabData;
  initialTab?: "fund" | "underlying";
  underlyingTabSubtitle?: React.ReactNode;
  panelTitle?: string;
  kind?: "equity" | "bond";
}

const TAB_PILL_CLASS: Record<FundHoldingsTabPill, string> = {
  TRS: "bg-[rgba(255,85,0,0.15)] text-[var(--etf-orange)] border-[rgba(255,85,0,0.3)]",
  "T-BILL": "bg-[rgba(0,178,158,0.12)] text-[#00b29e] border-[rgba(0,178,158,0.3)]",
  CASH: "bg-white/[0.06] text-[var(--etf-white3)] border-[var(--etf-border)]",
};

function formatPct(v: number): string {
  return `${v >= 0 ? "" : "−"}${Math.abs(v).toFixed(2)}%`;
}

const SECTOR_COLORS: Record<string, string> = {
  Technology: "#007c44",
  "Information Technology": "#007c44",
  Information: "#007c44",
  "Comm. Services": "#4a80d4",
  "Communication Services": "#4a80d4",
  Transportation: "#4a80d4",
  "Consumer Disc.": "#00c878",
  "Consumer Discretionary": "#00c878",
  "Retail Trade": "#00c878",
  "Media & Entertainment": "#00c878",
  "Consumer Staples": "#6b8e23",
  Industrials: "#1a2888",
  Manufacturing: "#1a2888",
  "Aerospace & Defense": "#1a2888",
  Financials: "#848484",
  Finance: "#848484",
  "Real Estate": "#b6bdd3",
  "Precious Metals": "#b6bdd3",
  Healthcare: "#9b7ddb",
  "Health Care": "#9b7ddb",
  Biotech: "#9b7ddb",
  Utilities: "#00b29e",
  Materials: "#8385b6",
  "Prof. Services": "#8385b6",
  "Waste Mgmt": "#8385b6",
  Energy: "#485465",
  Mining: "#485465",
  Commodities: "#485465",
  "Cash & Equivalents": "#ff5500",
  "Swap/Cash": "#ff5500",
  Unknown: "#666",
};

function sectorColor(sector: string): string {
  return SECTOR_COLORS[sector] ?? "#666";
}

export function FundHoldingsSidePanel({
  open,
  onClose,
  holdings,
  holdingsCount,
  dateLabel,
  ticker,
  fundName,
  showFounder,
  founderLookup,
  fundHoldingsTab,
  initialTab = "underlying",
  underlyingTabSubtitle,
  panelTitle,
  kind = "equity",
}: Props) {
  const isBond = kind === "bond";
  const [query, setQuery] = useState("");
  const panelRef = useRef<HTMLDivElement>(null);
  const closeBtnRef = useRef<HTMLButtonElement>(null);
  const bifurcated = Boolean(fundHoldingsTab);
  const [activeTab, setActiveTab] = useState<"fund" | "underlying">(initialTab);

  useEffect(() => {
    if (!open) {
      setQuery("");
      if (bifurcated) setActiveTab(initialTab);
    }
  }, [open, bifurcated, initialTab]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeBtnRef.current?.focus();
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Tab" || !panelRef.current) return;
      const focusable = panelRef.current.querySelectorAll<HTMLElement>(
        'button, input, [tabindex]:not([tabindex="-1"])',
      );
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!first || !last) return;
      const active = document.activeElement;
      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  const q = query.trim().toLowerCase();
  const filtered = q
    ? holdings.filter((h) => {
        const tickerMatch = h.ticker.toLowerCase().includes(q);
        const nameMatch = h.name.toLowerCase().includes(q);
        if (isBond) {
          const ratingMatch = (h.rating ?? "").toLowerCase().includes(q);
          return tickerMatch || nameMatch || ratingMatch;
        }
        return tickerMatch || nameMatch || h.sector.toLowerCase().includes(q);
      })
    : holdings;

  const gridCols = isBond
    ? "grid-cols-[28px_minmax(0,1fr)_84px_104px_64px_56px] max-sm:grid-cols-[24px_minmax(0,1fr)_56px_64px]"
    : showFounder
      ? "grid-cols-[28px_minmax(0,1fr)_92px_84px_84px_56px_84px] max-sm:grid-cols-[24px_minmax(0,1fr)_72px_64px]"
      : "grid-cols-[28px_minmax(0,1fr)_92px_84px_56px_84px] max-sm:grid-cols-[24px_minmax(0,1fr)_72px_64px]";

  return (
    <>
      <button
        type="button"
        aria-label="Close holdings panel"
        tabIndex={-1}
        onClick={onClose}
        className={`fixed inset-0 z-[900] bg-black/65 transition-opacity duration-300 ${
          open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
      />

      <aside
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={fundName ? `${fundName} holdings` : "Holdings"}
        className={`fixed top-0 right-0 z-[901] flex flex-col h-screen ${
          bifurcated ? "w-[min(600px,100vw)]" : "w-[min(560px,100vw)]"
        } max-sm:w-screen bg-[var(--etf-black2)] border-l border-[var(--etf-border)] overflow-hidden transition-transform duration-[350ms] ease-[cubic-bezier(0.32,0.72,0,1)] ${
          open ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="flex items-start justify-between gap-4 px-7 max-sm:px-4 pt-6 pb-5 border-b border-[var(--etf-border)] bg-[var(--etf-black2)] flex-shrink-0">
          <div className="min-w-0">
            <div className="font-[family-name:var(--font-instrument)] text-[12px] tracking-[0.2em] uppercase text-[var(--etf-orange)] mb-1.5">
              {ticker}
            </div>
            <div className="font-[family-name:var(--font-f37bolton)] text-[22px] tracking-[-0.01em] text-[var(--etf-white)] leading-tight">
              {panelTitle ?? (bifurcated ? "Fund Holdings" : "All Holdings")}
            </div>
            {!bifurcated && (
              <div className="font-[family-name:var(--font-instrument)] text-[12px] text-[var(--etf-white3)] mt-1">
                {holdingsCount} holdings &middot; As of {dateLabel}
              </div>
            )}
          </div>
          <button
            ref={closeBtnRef}
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="flex items-center justify-center w-9 h-9 bg-[var(--etf-black3)] border border-[var(--etf-border)] text-[var(--etf-white2)] text-base cursor-pointer flex-shrink-0 hover:bg-white/5 transition-colors"
          >
            &#10005;
          </button>
        </div>

        {bifurcated && (
          <div
            role="tablist"
            aria-label="Holdings views"
            className="flex border-b border-[var(--etf-border)] bg-[var(--etf-black2)] flex-shrink-0"
          >
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === "fund"}
              aria-controls="panel-fund-holdings"
              onClick={() => setActiveTab("fund")}
              className={`flex-1 py-3 bg-transparent border-0 border-b-2 font-[family-name:var(--font-instrument)] text-[12px] tracking-[0.12em] uppercase cursor-pointer transition-colors ${
                activeTab === "fund"
                  ? "border-[var(--etf-orange)] text-[var(--etf-orange)]"
                  : "border-transparent text-[var(--etf-white3)] hover:text-[var(--etf-white2)]"
              }`}
            >
              Fund Holdings
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === "underlying"}
              aria-controls="panel-underlying-holdings"
              onClick={() => setActiveTab("underlying")}
              className={`flex-1 py-3 bg-transparent border-0 border-b-2 font-[family-name:var(--font-instrument)] text-[12px] tracking-[0.12em] uppercase cursor-pointer transition-colors ${
                activeTab === "underlying"
                  ? "border-[var(--etf-orange)] text-[var(--etf-orange)]"
                  : "border-transparent text-[var(--etf-white3)] hover:text-[var(--etf-white2)]"
              }`}
            >
              Underlying Holdings
            </button>
          </div>
        )}

        {bifurcated && fundHoldingsTab && activeTab === "fund" && (
          <div
            id="panel-fund-holdings"
            role="tabpanel"
            aria-labelledby="tab-fund-holdings"
            className="flex flex-col flex-1 overflow-hidden"
          >
            <div className="px-7 max-sm:px-4 py-4 border-b border-[var(--etf-border2)] bg-[rgba(255,85,0,0.04)] flex-shrink-0">
              <div className="font-[family-name:var(--font-instrument)] text-[11px] tracking-[0.18em] uppercase text-[var(--etf-orange)] mb-1.5">
                {fundHoldingsTab.callout.label}
              </div>
              <div className="font-[family-name:var(--font-instrument)] text-[12px] text-[var(--etf-white3)] leading-[1.6] font-light">
                {fundHoldingsTab.callout.body}
              </div>
            </div>
            <div className="grid grid-cols-[minmax(0,1fr)_100px_100px] max-sm:grid-cols-[minmax(0,1fr)_72px_72px] gap-4 px-7 max-sm:px-4 py-2.5 border-b border-[var(--etf-border2)] bg-[var(--etf-black)] flex-shrink-0">
              <span className="font-[family-name:var(--font-instrument)] text-[11px] tracking-[0.12em] uppercase text-[var(--etf-white3)]">
                Instrument
              </span>
              <span className="font-[family-name:var(--font-instrument)] text-[11px] tracking-[0.12em] uppercase text-[var(--etf-white3)] text-right">
                Type
              </span>
              <span className="font-[family-name:var(--font-instrument)] text-[11px] tracking-[0.12em] uppercase text-[var(--etf-white3)] text-right">
                Weight
              </span>
            </div>
            <div className="overflow-y-auto flex-1">
              {fundHoldingsTab.rows.length === 0 ? (
                <div className="px-7 max-sm:px-4 py-10 font-[family-name:var(--font-instrument)] text-[13px] text-[var(--etf-white3)]">
                  {fundHoldingsTab.loadingText ?? "Holdings unavailable."}
                </div>
              ) : (
                fundHoldingsTab.rows.map((r) => (
                  <div
                    key={r.name}
                    className="grid grid-cols-[minmax(0,1fr)_100px_100px] max-sm:grid-cols-[minmax(0,1fr)_72px_72px] gap-4 items-center px-7 max-sm:px-4 py-4 border-b border-white/[0.05]"
                    style={r.highlight ? { background: "rgba(255,85,0,0.03)" } : undefined}
                  >
                    <div className="min-w-0 pr-2">
                      <div
                        className="font-[family-name:var(--font-instrument)] text-[13px] font-semibold tracking-[0.06em] mb-[3px] truncate"
                        style={{
                          color: r.highlight ? "var(--etf-orange)" : "var(--etf-white)",
                        }}
                      >
                        {r.name}
                      </div>
                      {r.description && (
                        <div className="font-[family-name:var(--font-instrument)] text-[11px] text-[var(--etf-white3)]">
                          {r.description}
                        </div>
                      )}
                    </div>
                    <div className="text-right">
                      {r.pill && (
                        <span
                          className={`font-[family-name:var(--font-instrument)] text-[11px] tracking-[0.04em] px-2 py-[2px] border ${TAB_PILL_CLASS[r.pill]}`}
                        >
                          {r.pill}
                        </span>
                      )}
                    </div>
                    <div className="font-[family-name:var(--font-instrument)] text-[13px] font-medium text-[var(--etf-white)] text-right tabular-nums">
                      {formatPct(r.weightPct)}
                    </div>
                  </div>
                ))
              )}
            </div>
            <div className="flex items-center justify-between gap-4 px-7 max-sm:px-4 py-3.5 border-t border-[var(--etf-border)] bg-[var(--etf-black)] flex-shrink-0">
              <span className="font-[family-name:var(--font-instrument)] text-[11px] text-[var(--etf-white3)]">
                {fundHoldingsTab.asOfLabel}
              </span>
              {fundHoldingsTab.ctaHref && fundHoldingsTab.ctaLabel && (
                <a
                  href={fundHoldingsTab.ctaHref}
                  className="font-[family-name:var(--font-instrument)] text-[11px] text-[var(--etf-orange)] no-underline border-b border-[rgba(255,85,0,0.3)] hover:border-[var(--etf-orange)] transition-colors"
                >
                  {fundHoldingsTab.ctaLabel}
                </a>
              )}
            </div>
          </div>
        )}

        <div
          id="panel-underlying-holdings"
          role={bifurcated ? "tabpanel" : undefined}
          className={`flex flex-col flex-1 overflow-hidden ${
            bifurcated && activeTab !== "underlying" ? "hidden" : ""
          }`}
        >
          {bifurcated && underlyingTabSubtitle && (
            <div className="px-7 max-sm:px-4 pt-3 bg-[var(--etf-black2)] flex-shrink-0">
              <div className="font-[family-name:var(--font-instrument)] text-[11px] text-[var(--etf-white3)] mb-2.5">
                {underlyingTabSubtitle}
              </div>
            </div>
          )}

          <div className="px-7 max-sm:px-4 py-3.5 border-b border-[var(--etf-border2)] bg-[var(--etf-black2)] flex-shrink-0">
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={isBond ? "Search ticker, issuer, or rating…" : "Search ticker or name…"}
              aria-label="Search holdings"
              className="w-full bg-[var(--etf-black3)] border border-[var(--etf-border)] text-[var(--etf-white)] font-[family-name:var(--font-instrument)] text-[13px] py-[9px] px-[14px] outline-none tracking-[0.04em] placeholder:text-[var(--etf-white3)]"
            />
          </div>

          <div
            className={`grid ${gridCols} gap-4 px-7 max-sm:px-4 py-2.5 border-b border-[var(--etf-border2)] bg-[var(--etf-black)] flex-shrink-0`}
          >
            <span className="font-[family-name:var(--font-instrument)] text-[11px] tracking-[0.12em] uppercase text-[var(--etf-white3)]">
              #
            </span>
            <span className="font-[family-name:var(--font-instrument)] text-[11px] tracking-[0.12em] uppercase text-[var(--etf-white3)]">
              {isBond ? "Issuer" : "Company"}
            </span>
            {isBond ? (
              <>
                <span className="font-[family-name:var(--font-instrument)] text-[11px] tracking-[0.12em] uppercase text-[var(--etf-white3)] max-sm:hidden">
                  Coupon
                </span>
                <span className="font-[family-name:var(--font-instrument)] text-[11px] tracking-[0.12em] uppercase text-[var(--etf-white3)]">
                  Maturity
                </span>
                <span className="font-[family-name:var(--font-instrument)] text-[11px] tracking-[0.12em] uppercase text-[var(--etf-white3)] max-sm:hidden">
                  Rating
                </span>
              </>
            ) : (
              <>
                <span className="font-[family-name:var(--font-instrument)] text-[11px] tracking-[0.12em] uppercase text-[var(--etf-white3)] max-sm:hidden">
                  Sector
                </span>
                <span className="font-[family-name:var(--font-instrument)] text-[11px] tracking-[0.12em] uppercase text-[var(--etf-white3)]">
                  Mkt Val
                </span>
                {showFounder && (
                  <span className="font-[family-name:var(--font-instrument)] text-[11px] tracking-[0.12em] uppercase text-[var(--etf-white3)] max-sm:hidden">
                    Founder
                  </span>
                )}
              </>
            )}
            <span className="font-[family-name:var(--font-instrument)] text-[11px] tracking-[0.12em] uppercase text-[var(--etf-white3)] text-right pr-4">
              Wgt
            </span>
            {!isBond && (
              <span className="font-[family-name:var(--font-instrument)] text-[11px] tracking-[0.12em] uppercase text-[var(--etf-white3)] max-sm:hidden">
                CUSIP
              </span>
            )}
          </div>

          <div className="flex-1 overflow-y-auto bg-[var(--etf-black2)]">
            {filtered.length === 0 ? (
              <div className="px-7 max-sm:px-4 py-10 font-[family-name:var(--font-instrument)] text-[13px] text-[var(--etf-white3)]">
                {q ? (
                  <>No holdings match &ldquo;{query}&rdquo;.</>
                ) : (
                  "Holdings data is currently unavailable. If the fund is live, please refresh in a moment."
                )}
              </div>
            ) : (
              filtered.map((h, i) => {
                const founder = h.founder ?? founderLookup?.[h.ticker.toUpperCase()];
                return (
                  <div
                    key={h.cusip || h.ticker}
                    className={`grid ${gridCols} gap-4 items-center px-7 max-sm:px-4 py-3 border-b border-white/[0.05] hover:bg-[rgba(255,85,0,0.05)] transition-colors ${
                      i % 2 ? "bg-white/[0.015]" : ""
                    }`}
                  >
                    <span className="font-[family-name:var(--font-instrument)] text-[11px] text-[var(--etf-white3)] tabular-nums">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <div className="min-w-0 pr-3">
                      <div className="font-[family-name:var(--font-instrument)] text-[13px] font-semibold text-[var(--etf-white)] tracking-[0.06em] truncate">
                        {h.ticker}
                      </div>
                      <div className="font-[family-name:var(--font-instrument)] text-[12px] text-[var(--etf-white3)] mt-0.5 truncate">
                        {h.name}
                      </div>
                    </div>
                    {isBond ? (
                      <>
                        <div className="font-[family-name:var(--font-instrument)] text-[13px] text-[var(--etf-white2)] tabular-nums max-sm:hidden">
                          {h.coupon || "—"}
                        </div>
                        <div className="font-[family-name:var(--font-instrument)] text-[13px] text-[var(--etf-white2)] tabular-nums">
                          {h.maturity || "—"}
                        </div>
                        <div className="font-[family-name:var(--font-instrument)] text-[12px] text-[var(--etf-white3)] tabular-nums max-sm:hidden">
                          {h.rating || "—"}
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="flex items-center gap-1.5 max-sm:hidden">
                          <span
                            className="inline-block w-[7px] h-[7px] rounded-full flex-shrink-0"
                            style={{ background: sectorColor(h.sector) }}
                          />
                          <span className="font-[family-name:var(--font-instrument)] text-[11px] text-[var(--etf-white3)] truncate">
                            {h.sector}
                          </span>
                        </div>
                        <div className="font-[family-name:var(--font-instrument)] text-[13px] text-[var(--etf-white2)] tabular-nums">
                          {h.marketValue || "—"}
                        </div>
                        {showFounder && (
                          <div className="font-[family-name:var(--font-instrument)] text-[11px] text-[var(--etf-white3)] truncate max-sm:hidden">
                            {founder || "—"}
                          </div>
                        )}
                      </>
                    )}
                    <div className="font-[family-name:var(--font-instrument)] text-[13px] font-medium text-[var(--etf-white)] text-right tabular-nums pr-4">
                      {h.pct == null ? "—" : `${h.pct.toFixed(2)}%`}
                    </div>
                    {!isBond && (
                      <div className="font-[family-name:var(--font-instrument)] text-[12px] text-[var(--etf-white3)] tabular-nums truncate max-sm:hidden">
                        {h.cusip || "—"}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>

          <div className="flex items-center justify-between gap-4 px-7 max-sm:px-4 py-4 border-t border-[var(--etf-border)] bg-[var(--etf-black)] flex-shrink-0">
            <span className="font-[family-name:var(--font-instrument)] text-[12px] text-[var(--etf-white3)]">
              Showing <span className="text-[var(--etf-white)]">{filtered.length}</span> of{" "}
              <span className="text-[var(--etf-white)]">{holdingsCount}</span> holdings
            </span>
          </div>
        </div>
      </aside>
    </>
  );
}
